import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { FormikHelpers } from 'formik';
import i18n from '../../utilities/i18n';
import { ROUTES } from '../../constants';
import mediator from '../../utilities/mediator';
import { getAuthenticationError } from '../../utilities/auth';
import UpdatePassword, { UpdatePasswordFormValues } from './UpdatePassword';

/**
 * Handles password reset via a one-time token delivered by email.
 * Reads the token from query params, clears it from the URL, then composes UpdatePassword.
 * @returns {React.JSX.Element}
 */
export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [token] = useState(searchParams.get('token'));
  const [errorMessage, setErrorMessage] = useState('');
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  useEffect(() => {
    if (!token) {
      setErrorMessage(i18n.t('Auth_PasswordReset.errorInvalidToken'));
    } else {
      navigate(ROUTES.AUTH.RESET_PASSWORD, { replace: true });
    }
  }, [token, navigate]);

  const onSubmit = useCallback(
    (
      values: UpdatePasswordFormValues,
      helpers: FormikHelpers<UpdatePasswordFormValues>,
    ) =>
      mediator
        .request('auth:resetPassword', {
          data: { token, password: values.password },
        })
        .then(() => {
          setErrorMessage('');
          helpers.resetForm();
          setSuccessModalOpen(true);
        })
        .catch((error) => {
          if (error.status === 400) {
            if (
              !!token &&
              error.data?.error?.message?.includes('invalidOrExpiredToken')
            ) {
              setErrorMessage(i18n.t('Auth_PasswordReset.errorInvalidToken'));
            } else {
              setErrorMessage(
                getAuthenticationError(
                  error.data.data,
                  'Auth_PasswordReset.errorGeneric',
                ),
              );
            }
          } else {
            setErrorMessage(
              getAuthenticationError(error, 'Auth_PasswordReset.errorGeneric'),
            );
          }
        }),
    [token],
  );

  return (
    <UpdatePassword
      title={i18n.t('Auth_PasswordReset.title')}
      onSubmit={onSubmit}
      errorMessage={errorMessage}
      successModalOpen={successModalOpen}
    />
  );
}
